/**
 * view.js — Entry Point
 *
 * Bootstraps the Card Registration block. All logic lives in dedicated modules:
 *
 *  field-config.js      → Field definitions (selectors, formatters, validators)
 *  form-fields.js       → Field listeners (formatting, inline validation, errors)
 *  code-verification.js → 6-digit code UX (generate, validate, resend, input UX)
 *  form-submission.js   → Form submit handler (validation, API call, loading state)
 *  api.js               → API wrapper
 */

import CardRegistrationAPI from './api';
import { initFormFields, updateSlideInteractivity } from './form-fields';
import { initCodeVerification } from './code-verification';
import { initFormSubmission } from './form-submission';

import {
	loadFormData,
	getSavedData,
	loadCurrentStep,
	saveCurrentStep,
} from './form-persistence';
import { initCardCheck } from './card-check';
import { playSuccessAnimation } from './success-animation';
import { isMobile } from '../../../utilities/General';

document.addEventListener('DOMContentLoaded', () => {
	const api = new CardRegistrationAPI();

	document
		.querySelectorAll('.wp-block-cancan-card-registration')
		.forEach((formWrapper) => {
			const swiper = initSlider(formWrapper);
			const form = formWrapper.querySelector('.cr-form');
			if (!form) return;

			const elements = initFormFields(form);

			// Restore session state
			const savedStep = loadCurrentStep();
			const saved = getSavedData();

			if (savedStep === 1 && saved) {
				// We are resuming a verification session
				loadFormData(elements);
				swiper?.slideTo(1, 0);
			} else if (saved?.cardNumber && elements.cardNumber) {
				// Just restoring a draft
				elements.cardNumber.value = saved.cardNumber;
			}


			initCardCheck(form, swiper, api, elements);

			initCodeVerification(form, swiper, api, elements);
			initFormSubmission(form, elements, swiper, api);

			// Initial interactivity check
			updateSlideInteractivity(form, swiper);

			swiper?.on('slideChange', () => {
				if (isMobile()) {
					form.scrollIntoView({ behavior: 'smooth' });
				}
				updateSlideInteractivity(form, swiper);
				saveCurrentStep(swiper.activeIndex);
			});

			// Success handling
			form.addEventListener('cardRegistrationSuccess', (e) => {
				console.log(e);
				if (e.detail.formId !== form.id) return;

				// 1. Lock API to prevent any further requests
				api.lock();

				// 2. Disable all UI interaction natively
				form.inert = true;
				form.classList.add('cr-form--success');

				// 3. Play success animation
				const successEl = formWrapper.querySelector('.cr-success');

				if (successEl) {
					playSuccessAnimation();
				}
			});

			// Back button — slide to previous step
			const backBtn = form.querySelector('.cr-back-btn');
			backBtn?.addEventListener('click', () => swiper?.slidePrev());
		});
});

// ─── Slider ──────────────────────────────────────────────────────────────────

/**
 * Initialise the Swiper instance for the given form wrapper.
 * Auto-height is kept in sync with slide content via a ResizeObserver.
 *
 * @param   {HTMLElement}             formWrapper
 * @returns {import('swiper').default | null}
 */
function initSlider(formWrapper) {
	const swiperEl = formWrapper.querySelector('.swiper');
	if (!swiperEl) return null;

	const swiper = new Swiper(swiperEl, {
		slidesPerView: 1,
		spaceBetween: 10,
		loop: false,
		allowTouchMove: false,
		autoHeight: true,
		navigation: {
			nextEl: 'button.next-slide',
			prevEl: 'button.prev-slide',
		},
	});

	// Keep autoHeight updated whenever slide content height changes
	// (e.g. validation errors appearing / disappearing).
	const resizeObserver = new ResizeObserver(() => swiper.updateAutoHeight(0));
	formWrapper
		.querySelectorAll('.swiper-slide')
		.forEach((slide) => resizeObserver.observe(slide));

	return swiper;
}
